import RefundModalOverviewFooter from '@/components/manage/refund/RefundModalOverviewFooter';
import RefundModalSelectionFooter from '@/components/manage/refund/RefundModalSelectionFooter';
import ResponsiveDialog from '@/components/ResponsiveDialog';
import { manageBookingRefundLoadingSelector } from '@/features/loading/loadingSelectors';
import {
  confirmRefundOffer,
  deleteRefundOffer,
  getManageFlowBookingById,
  resetRefundSelection,
} from '@/features/manage/manageActions';
import { manageFlowBookingSelector } from '@/features/manage/manageSelectors';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import { showErrorToast } from '@/utils/errorToast';
import { Typography } from '@packages/webshop-design-system';
import { type FC, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

interface RefundModalProps {
  isOpen: boolean;
  refundSelection: FC;
  refundOverview: FC;
  selected: number;
  chooseToRefundText:
    | 'chooseTripsToRefund'
    | 'chooseTravelPassesToRefund'
    | 'selectExtras';
  refundText: 'refundTrips' | 'refundTravelPasses' | 'refundExtras';
  refundInitiatedText: 'refundInitiated' | 'extrasRefundInitiated';
  showBookingCode?: boolean;
  onInitiateRefund: () => Promise<void>;
  onClose: () => void;
}

enum RefundStep {
  selection,
  confirmation,
}

const RefundModal: FC<RefundModalProps> = ({
  isOpen,
  refundSelection: RefundSelection,
  refundOverview: RefundOverview,
  selected,
  chooseToRefundText,
  refundText,
  refundInitiatedText,
  showBookingCode = false,
  onInitiateRefund,
  onClose,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector(manageBookingRefundLoadingSelector);
  const [currentStep, setCurrentStep] = useState<RefundStep>(
    RefundStep.selection
  );
  const currentBooking = useSelector(manageFlowBookingSelector);

  const handleClose = useCallback(() => {
    onClose();
    dispatch(resetRefundSelection());
    setCurrentStep(RefundStep.selection);
  }, [dispatch, onClose]);

  const handleOpenChange = useCallback(
    async (open: boolean) => {
      if (!open && currentStep === RefundStep.confirmation) {
        await dispatch(deleteRefundOffer()).unwrap();
      }

      handleClose();
    },
    [currentStep, dispatch, handleClose]
  );

  const handleBackToSelection = useCallback(async () => {
    await dispatch(deleteRefundOffer()).unwrap();
    setCurrentStep(RefundStep.selection);
  }, [dispatch]);

  const handleInitiateRefund = useCallback(async () => {
    try {
      await onInitiateRefund();
      setCurrentStep(RefundStep.confirmation);
    } catch (e) {
      showErrorToast(
        <TransAlert i18nKey="refundFailed" />,
        e as ApiErrorResponse
      );
    }
  }, [onInitiateRefund]);

  const handleConfirmRefund = useCallback(async () => {
    try {
      await dispatch(confirmRefundOffer()).unwrap();
    } catch (e) {
      showErrorToast(
        <TransAlert i18nKey="refundFailed" />,
        e as ApiErrorResponse
      );
      return;
    }

    try {
      await dispatch(getManageFlowBookingById(currentBooking?.id)).unwrap();
    } catch (e) {
      showErrorToast(
        <TransAlert i18nKey="fetchingBookingFailed" />,
        e as ApiErrorResponse
      );
    }

    toast.success(<TransText i18nKey={refundInitiatedText} />);
    handleClose();
  }, [currentBooking, dispatch, handleClose, refundInitiatedText]);

  const { title, content, footer } = useMemo(() => {
    const bookingCode = showBookingCode && currentBooking?.code && (
      <Typography variant="body1" className="text-neutral">
        <TransText i18nKey="booking" /> {currentBooking.code}
      </Typography>
    );

    if (currentStep === RefundStep.selection) {
      return {
        title: (
          <div className="flex flex-col">
            <TransText i18nKey={chooseToRefundText} />
            {bookingCode}
          </div>
        ),
        content: <RefundSelection />,
        footer: (
          <RefundModalSelectionFooter
            handleClose={handleClose}
            handleInitiateRefund={handleInitiateRefund}
            selected={selected}
            loading={loading}
          />
        ),
      };
    }

    return {
      title: (
        <div className="flex flex-col">
          <TransText i18nKey={refundText} values={{ count: selected }} />
          {bookingCode}
        </div>
      ),
      content: <RefundOverview />,
      footer: (
        <RefundModalOverviewFooter
          handleConfirmRefund={handleConfirmRefund}
          handleBackToSelection={handleBackToSelection}
          loading={loading}
        />
      ),
    };
  }, [
    RefundOverview,
    RefundSelection,
    chooseToRefundText,
    currentBooking,
    showBookingCode,
    currentStep,
    handleBackToSelection,
    handleClose,
    handleConfirmRefund,
    handleInitiateRefund,
    refundText,
    selected,
    loading,
  ]);

  return (
    <ResponsiveDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      title={title}
      content={content}
      footer={footer}
    />
  );
};

export default RefundModal;
